import { DashboardLayout } from "../../../components/layout/components/DashboardLayout";
import { Navbar } from "../../../components/layout/components/Navbar";
import { JobCard } from "../components/JobCard";
import { useEffect, useRef, useState } from "react";
import { FilterJob } from "../components/FilterJob";
import { useGetUserInfo } from "../../../hooks/useGetUserInfo";
import { useGetTalentCount } from "../../../features/home/hooks/useGetTalentCount";
import { useTour } from "../../../components/layout/components/tours/TourGuideContext";
import { useGetJobs, QUERY_KEY_FOR_JOBS } from "../hooks/useGetJobs";
import { usePagination } from "../../../hooks/usePagination";
import { useLikeJob } from "../hooks/useLikeJob";
import { useBookmarkJob } from "../hooks/useBookmark";
import {
  QUERY_KEY_FOR_SUGGESTED_JOBS,
  useTalentsSuggestedJobs,
} from "../hooks/useSuggestedJobsfoTalents";
import { useQueryClient } from "react-query";
import { TalentJobsLoading } from "../../../components/layout/components/loaders/Shimmer";
import { useGlobalContext } from "../../../stateManagers/GlobalContext";
import { IJobsProps } from "../types";
import {
  ensureMetaPixelForTrackedJob,
  trackMetaJobLeadForListing,
  trackMetaJobsDirectoryPageView,
} from "../../../utils/metaPixelJob";
import { Link } from "react-router-dom";
import { routePath } from "../../../config/routeMgt/routePath";
import { useProfileCompletion } from "../../../features/settings/hooks/useProfileCompletion";

export const TalentJobs = () => {
  const { token, userInfo } = useGetUserInfo();
  const { data: talentCount } = useGetTalentCount();
  const { incompleteSections, isLoading: isProfileLoading } =
    useProfileCompletion();
  const [openNewJob, setOpenNewJob] = useState(false);
  const [jobId, setJobId] = useState<number>();
  const [jobFilter, setJobFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<
    "all" | "liked" | "bookmarked" | "suggested"
  >("all");

  const { running } = useTour();
  const { pagination, onChange } = usePagination();
  const queryClient = useQueryClient();
  const { filterJobState } = useGlobalContext();
  const jobsDirectoryPageViewSent = useRef(false);

  const isProfileComplete =
    incompleteSections.length === 0 && !isProfileLoading;
  const showBanner = !isProfileLoading && incompleteSections.length > 0;

  const handleJob = (id: number) => {
    setJobId(id);
    setOpenNewJob(true);
  };

  const isSuggestedTab = activeTab === "suggested";

  const {
    data: jobsData,
    isLoading: isJobsLoading,
    refetch,
  } = useGetJobs({
    pagination,
    search: searchTerm,
    liked_by_user: !userInfo?.recruiter && activeTab === "liked" ? 1 : 0,
    bookmarked_by_user:
      !userInfo?.recruiter && activeTab === "bookmarked" ? 1 : 0,
    title: filterJobState.title,
    location_country: filterJobState.location_country,
    location_city: filterJobState.location_city,
    employment_type: filterJobState.employment_type,
    work_model: filterJobState.work_model,
    closed: filterJobState.closed,
    experience_level: filterJobState.experience_level,
    sector: filterJobState.sector,
    jobposted: filterJobState.jobposted,
    start_date: filterJobState.start_date,
    end_date: filterJobState.end_date,
  });

  const { data: suggestedData, isLoading: isSuggestedLoading } =
    useTalentsSuggestedJobs(isSuggestedTab);

  const normalizedSuggestedJobs = (suggestedData?.data ?? [])
    .map((item: any) => item.listing)
    .filter(Boolean);

  const displayData = isSuggestedTab
    ? normalizedSuggestedJobs
    : (jobsData?.data ?? []);

  const isLoading = isSuggestedTab ? isSuggestedLoading : isJobsLoading;

  const { mutate: likeMutate } = useLikeJob();
  const { mutate: bookmarkMutate } = useBookmarkJob();

  useEffect(() => {
    onChange({ current: 1, pageSize: pagination.pageSize });
  }, [JSON.stringify(filterJobState)]);

  useEffect(() => {
    if (!isSuggestedTab) {
      refetch();
    }
  }, [filterJobState, activeTab, searchTerm, refetch]);

  useEffect(() => {
    if (userInfo?.talent && talentCount?.profile?.total >= 0) {
      setOpenNewJob(true);
    }
  }, [userInfo?.talent, talentCount]);

  useEffect(() => {
    void ensureMetaPixelForTrackedJob();
  }, []);

  useEffect(() => {
    if (isLoading || jobsDirectoryPageViewSent.current) return;
    jobsDirectoryPageViewSent.current = true;
    trackMetaJobsDirectoryPageView();
  }, [isLoading]);

  if (isLoading) {
    return <TalentJobsLoading />;
  }

  return (
    <DashboardLayout>
      <FilterJob open={jobFilter} handleClose={() => setJobFilter(false)} />

      <div className="mx-4 mt-4" data-tour="job-board-title">
        <Navbar
          description={userInfo?.current_team?.name || "View available Jobs"}
          title="Jobs"
        />

        {showBanner && (
          <div className="mt-4 bg-red-50 border border-red-300 rounded-md px-4 py-2 shadow-sm">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-red-700 font-medium">
                ⚠️ Your profile needs attention:
              </span>

              <span className="text-red-600">
                Complete {incompleteSections.join(", ")} to apply for jobs
              </span>

              <div className="flex items-center gap-2">
                {incompleteSections.map((section) => (
                  <Link
                    key={section}
                    to={
                      section === "Basic Details"
                        ? routePath.settings
                        : section === "Education"
                          ? routePath.education
                          : routePath.experience
                    }
                    className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 transition"
                  >
                    Go to {section} →
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="flex gap-6 mt-6 job-tabs-container mx-4"
        data-tour="job-tabs"
      >
        <div className="w-56 bg-white border border-gray-200 rounded-xl p-4 space-y-2">
          {[
            { id: "all", label: "All Jobs" },
            { id: "liked", label: "Applied Jobs" },
            { id: "bookmarked", label: "Saved Jobs" },
            { id: "suggested", label: "Suggested Jobs" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`w-full text-left py-2 px-3 rounded-lg ${
                activeTab === tab.id
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {!isSuggestedTab && (
            <div className="flex justify-between items-center bg-white border border-gray-200 rounded-xl px-4 py-3 mb-6 shadow-sm">
              <div className="relative w-96">
                <i className="ri-search-line absolute left-3 top-3 text-gray-500 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search Jobs"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-2 focus:outline-none"
                />
              </div>

              <button
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm hover:bg-gray-50"
                onClick={() => setJobFilter(true)}
              >
                <i className="ri-filter-3-line text-gray-700"></i>
                <span className="text-gray-700">Filters</span>
              </button>
            </div>
          )}

          {isSuggestedTab && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-6">
              <p className="text-blue-700 text-sm font-medium">
                These jobs are matched to your profile based on your skills and
                experience.
              </p>
            </div>
          )}

          <div className="mt-8 max-h-[calc(100vh-260px)] overflow-y-auto pr-2">
            <div className="grid grid-cols-1 gap-6">
              {displayData.length > 0 ? (
                displayData.map((job: IJobsProps) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    handleJob={handleJob}
                    hideApplyButton={activeTab === "liked"}
                    likeJob={(id, hasLiked, onSuccess, onError) => {
                      likeMutate(
                        { ...job, token, has_liked: hasLiked },
                        {
                          onSuccess: () => {
                            queryClient.invalidateQueries([QUERY_KEY_FOR_JOBS]);
                            if (!isSuggestedTab) {
                              queryClient.invalidateQueries([
                                QUERY_KEY_FOR_SUGGESTED_JOBS,
                              ]);
                            }
                            trackMetaJobLeadForListing({
                              id: job.id,
                              slug: job.slug,
                              title: job.title,
                            });
                            if (onSuccess) onSuccess();
                          },
                          onError: (error) => {
                            console.error("Failed to apply:", error);
                            if (onError) onError();
                          },
                        },
                      );
                    }}
                    handleBookmark={(id, hasBookmarked) => {
                      bookmarkMutate(
                        { ...job, token, has_bookmarked: hasBookmarked },
                        {
                          onSuccess: () => {
                            queryClient.invalidateQueries([QUERY_KEY_FOR_JOBS]);
                            queryClient.invalidateQueries([
                              QUERY_KEY_FOR_SUGGESTED_JOBS,
                            ]);
                          },
                        },
                      );
                    }}
                    isProfileComplete={isProfileComplete}
                    incompleteSections={incompleteSections}
                  />
                ))
              ) : (
                <p className="text-gray-500 text-center py-10">
                  {isSuggestedTab
                    ? "No suggested jobs yet. Complete your profile to get personalized matches."
                    : "No Job Found"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
